import { STORE_ORIGIN } from '../config';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { FiShoppingCart, FiZoomIn, FiShare2, FiX } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const BASE_URL = STORE_ORIGIN;
const PRODUCT_IMAGES_URL = `${BASE_URL}/admin/uploads/products`;
const ADDITIONAL_IMAGES_URL = `${BASE_URL}/admin/uploads/products/additional`;

/**
 * Helper: Parse size name to extract numeric value or convert alphanumeric to sortable value
 */
function getSortValueForSize(sizeName = '') {
  if (!sizeName) return Number.MAX_SAFE_INTEGER;
  
  const name = sizeName.trim().toUpperCase();
  
  // Handle numeric sizes (like "100", "200", "300")
  const numericMatch = name.match(/(\d+(\.\d+)?)/);
  if (numericMatch) {
    return parseFloat(numericMatch[1]);
  }
  
  // Handle alphanumeric sizes with common patterns
  const sizeMap = {
    'XXS': 0, 'XS': 10, 'S': 20, 'M': 30, 'L': 40, 'XL': 50, 'XXL': 60, 'XXXL': 70,
    'XS-S': 15, 'S-M': 25, 'M-L': 35, 'L-XL': 45, 'XL-XXL': 55,
    'ONE SIZE': 5, 'OSFA': 5, 'UNIVERSAL': 5,
    '2XS': 5, '3XS': 2, '4XS': 1,
    '2XL': 55, '3XL': 65, '4XL': 75, '5XL': 85
  };
  
  // Check exact matches first
  if (sizeMap[name] !== undefined) {
    return sizeMap[name];
  }
  
  // Check for partial matches
  for (const [key, value] of Object.entries(sizeMap)) {
    if (name.includes(key)) {
      return value;
    }
  }
  
  // Fallback: sort alphabetically
  return name.charCodeAt(0) * 100;
}

/**
 * Helper: Format size display name
 */
function formatSizeDisplay(name = '') {
  if (!name) return '';
  
  // Keep numeric sizes as-is
  if (/^\d+(\.\d+)?$/.test(name)) {
    return name;
  }
  
  // Convert to uppercase for consistency
  const upper = name.toUpperCase();
  
  // Common size abbreviations
  const displayMap = {
    'XS': 'XS', 'S': 'S', 'M': 'M', 'L': 'L', 'XL': 'XL',
    'XXS': 'XXS', 'XXL': 'XXL', 'XXXL': 'XXXL',
    'ONE SIZE': 'One Size', 'OSFA': 'One Size',
    '2XS': '2XS', '3XS': '3XS', '4XS': '4XS',
    '2XL': '2XL', '3XL': '3XL', '4XL': '4XL', '5XL': '5XL',
    'XS-S': 'XS-S', 'S-M': 'S-M', 'M-L': 'M-L', 'L-XL': 'L-XL', 'XL-XXL': 'XL-XXL'
  };
  
  return displayMap[upper] || upper;
}

/**
 * ColorSwatch component
 */
const ColorSwatch = ({ codes = [], size = 36, className = '' }) => {
  const fallback = '#cccccc';
  if (!Array.isArray(codes) || codes.length === 0) {
    return (
      <div
        className={`rounded-full border ${className}`}
        style={{ width: size, height: size, background: fallback }}
      />
    );
  }
  if (codes.length === 1) {
    return (
      <div
        className={`rounded-full border ${className}`}
        style={{ width: size, height: size, background: codes[0] || fallback }}
      />
    );
  }
  // two or more codes -> use first two for half/half
  const c1 = codes[0] || fallback;
  const c2 = codes[1] || c1;
  return (
    <div
      className={`rounded-full border overflow-hidden ${className}`}
      style={{ width: size, height: size }}
    >
      <div style={{ display: 'flex', width: '100%', height: '100%' }}>
        <div style={{ width: '50%', height: '100%', background: c1 }} />
        <div style={{ width: '50%', height: '100%', background: c2 }} />
      </div>
    </div>
  );
};

const ProductDetailPage = () => {
  const { productSlug } = useParams();
  const navigate = useNavigate();
  const { addToCart, itemCount } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [mainImage, setMainImage] = useState('');
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [quantity, setQuantity] = useState(1);

  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
  const [showLightbox, setShowLightbox] = useState(false);
  const [lightboxImage, setLightboxImage] = useState('');

  const imageRef = useRef(null);
  const zoomRef = useRef(null);

  const location = useLocation();
  const { fromSubcategory, categoryId, subcategoryId } = location.state || {};

  const fetchProduct = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await axios.get(`${BASE_URL}/api/get_product.php?product_slug=${productSlug}`);
      if (!res.data || !res.data.success) throw new Error(res.data?.error || 'Product not found');

      const productData = res.data.data.product;
      
      console.log("Full product data:", productData); // Debug log to see structure
      console.log("Product SKU:", productData.sku); // Debug log for SKU
      console.log("Variations:", productData.variations); // Debug log for variations

      // Build image URLs list (main + additional)
      const allImages = [productData.main_image, ...(productData.images || []).map(i => i.path)].filter(Boolean);
      const images = allImages.map(img =>
        img.includes('additional')
          ? `${ADDITIONAL_IMAGES_URL}/${img.split('/').pop()}`
          : `${PRODUCT_IMAGES_URL}/${img.split('/').pop()}`
      );

      // Normalize variations
      const variations = (productData.variations || []).map(v => {
        const color = v.color ? { ...v.color, codes: Array.isArray(v.color.codes) ? v.color.codes : [] } : null;
        const size = v.size ? { ...v.size } : null;
        const variationImage = v.image ? (v.image.includes('variations') ? `${PRODUCT_IMAGES_URL}/variations/${v.image.split('/').pop()}` : v.image.includes('additional') ? `${ADDITIONAL_IMAGES_URL}/${v.image.split('/').pop()}` : `${PRODUCT_IMAGES_URL}/${v.image.split('/').pop()}`) : null;
        return {
          ...v,
          color,
          size,
          image: variationImage || (productData.main_image ? `${PRODUCT_IMAGES_URL}/${productData.main_image.split('/').pop()}` : null),
        };
      });

      // Collect unique colors and sizes from variations
      const colorsMap = new Map();
      const sizesMap = new Map();
      variations.forEach(v => {
        if (v.color && !colorsMap.has(v.color.color_id)) colorsMap.set(v.color.color_id, v.color);
        if (v.size && !sizesMap.has(v.size.size_id)) sizesMap.set(v.size.size_id, v.size);
      });

      // Sort sizes properly (ascending order)
      const sizesList = Array.from(sizesMap.values());
      sizesList.sort((a, b) => {
        const sortA = getSortValueForSize(a.name);
        const sortB = getSortValueForSize(b.name);
        
        if (typeof sortA === 'number' && typeof sortB === 'number') {
          return sortA - sortB;
        }
        
        if (typeof sortA === 'number' && typeof sortB !== 'number') return -1;
        if (typeof sortA !== 'number' && typeof sortB === 'number') return 1;
        
        return (a.name || '').localeCompare(b.name || '');
      });

      const processed = {
        ...productData,
        main_image: productData.main_image ? `${PRODUCT_IMAGES_URL}/${productData.main_image.split('/').pop()}` : null,
        images,
        variations,
        availableColors: Array.from(colorsMap.values()),
        availableSizes: sizesList,
        product_sku: productData.sku, // Store main product SKU separately
      };

      console.log("Processed product with SKU:", processed.product_sku);
      console.log("Processed sizes:", sizesList.map(s => ({ name: s.name, sort: getSortValueForSize(s.name) })));

      setProduct(processed);
      setMainImage(processed.main_image || processed.images[0] || '');

      // default selection: pick first variation that has stock>0 if possible, else first variation
      let defaultVariation = processed.variations.find(v => (v.stock || 0) > 0) || processed.variations[0] || { variation_id: null, price: processed.base_price, stock: processed.quantity, image: processed.main_image, sku: processed.sku };
      if (defaultVariation) {
        setSelectedVariation(defaultVariation);
        if (defaultVariation.color) setSelectedColor(defaultVariation.color);
        if (defaultVariation.size) setSelectedSize(defaultVariation.size);
      }

      setLoading(false);
    } catch (err) {
      console.error('Error fetching product:', err);
      setError(err.message || 'Failed to load product details');
      setLoading(false);
    }
  }, [productSlug]);

  useEffect(() => { fetchProduct(); }, [fetchProduct]);

  const handleBack = useCallback(() => {
    if (fromSubcategory) navigate(`/category/${categoryId}/subcategory/${subcategoryId}`);
    else navigate(-1);
  }, [fromSubcategory, categoryId, subcategoryId, navigate]);

  const handleColorSelect = useCallback((color) => {
    if (!product) return;
    setSelectedColor(color);

    let match = product.variations.find(v => v.color?.color_id === color.color_id && (!selectedSize || v.size?.size_id === selectedSize.size_id) && (v.stock || 0) > 0);
    if (!match) match = product.variations.find(v => v.color?.color_id === color.color_id && (!selectedSize || v.size?.size_id === selectedSize.size_id));
    if (!match) match = product.variations.find(v => v.color?.color_id === color.color_id);

    if (match) {
      setSelectedVariation(match);
      setMainImage(match.image || product.main_image || '');
      if (match.size) setSelectedSize(match.size);
    } else {
      setSelectedVariation(null);
    }
  }, [product, selectedSize]);

  const handleSizeSelect = useCallback((size) => {
    if (!product) return;
    setSelectedSize(size);

    let match = product.variations.find(v => v.size?.size_id === size.size_id && (!selectedColor || v.color?.color_id === selectedColor.color_id) && (v.stock || 0) > 0);
    if (!match) match = product.variations.find(v => v.size?.size_id === size.size_id && (!selectedColor || v.color?.color_id === selectedColor.color_id));
    if (!match) match = product.variations.find(v => v.size?.size_id === size.size_id);

    if (match) {
      setSelectedVariation(match);
      setMainImage(match.image || product.main_image || '');
      if (match.color) setSelectedColor(match.color);
    } else {
      setSelectedVariation(null);
    }
  }, [product, selectedColor]);

  const handleAddToCart = useCallback(() => {
    if (!product || !selectedVariation) return;
    try {
      addToCart(product, selectedVariation, quantity);
      toast.success(
        <div>
          <p className="font-medium">Added to Cart!</p>
          <p className="text-sm">{product.name}</p>
          <p className="text-sm">
            {selectedSize ? formatSizeDisplay(selectedSize.name) : 'One Size'}
            {selectedColor ? ` - ${selectedColor.name}` : ''}
          </p>
        </div>,
        { position: 'top-right', autoClose: 3000 }
      );
    } catch (err) {
      console.error('Add to cart error', err);
      toast.error('Failed to add item to cart', { position: 'top-right', autoClose: 3000 });
    }
  }, [product, selectedVariation, quantity, addToCart, selectedSize, selectedColor]);

  const handleBuyNow = useCallback(() => {
    if (!product || !selectedVariation) return;
    try {
      addToCart(product, selectedVariation, quantity);
      navigate('/checkout');
    } catch (err) {
      console.error('Buy now error', err);
      toast.error('Failed to proceed to checkout', { position: 'top-right', autoClose: 3000 });
    }
  }, [product, selectedVariation, quantity, addToCart, navigate]);

  const handleQuantityChange = useCallback((newQuantity) => {
    const maxQty = selectedVariation?.stock ?? product?.quantity ?? 0;
    const qty = Math.max(1, Math.min(newQuantity, maxQty));
    setQuantity(qty);
  }, [product, selectedVariation]);

  const shareOnWhatsApp = useCallback(() => {
    if (!product) return;
    const selectedOptions = [selectedColor?.name, selectedSize ? formatSizeDisplay(selectedSize.name) : null].filter(Boolean).join(' - ');
    const message = `Check out this product: ${product.name}${selectedOptions ? ` (${selectedOptions})` : ''}\n\n${window.location.href}`;
    window.open(`https://wa.me/97431282831?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
  }, [product, selectedColor, selectedSize]);

  const handleMouseMove = useCallback((e) => {
    if (!imageRef.current || !zoomRef.current) return;
    const { left, top, width, height } = imageRef.current.getBoundingClientRect();
    const x = ((e.clientX - left) / width) * 100;
    const y = ((e.clientY - top) / height) * 100;
    setZoomPosition({ x, y });
  }, []);

  const toggleZoom = useCallback(() => setIsZoomed(z => !z), []);
  const openLightbox = useCallback((img) => { setLightboxImage(img); setShowLightbox(true); }, []);
  const closeLightbox = useCallback(() => setShowLightbox(false), []);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center p-6 bg-white rounded-lg shadow-md">
        <h2 className="text-xl font-semibold text-red-600 mb-4">{error}</h2>
        <Link to="/" className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-lg transition-colors">Return to Home</Link>
      </div>
    </div>
  );

  if (!product) return null;

  const inStock = (selectedVariation?.stock || product.quantity || 0) > 0;
  const maxQuantity = selectedVariation?.stock || product.quantity || 1;
  
  // Always use main product SKU, not variation SKU
  const displaySKU = product.product_sku || product.sku || product.product_id;

  return (
    <div className="bg-gray-50 min-h-screen">
      <ToastContainer />

      {/* HEADER */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <button onClick={handleBack} className="flex items-center text-gray-600 hover:text-gray-900 font-medium" aria-label="Go back">
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/></svg>Back
          </button>
          <div className="flex items-center gap-4">
            <button onClick={shareOnWhatsApp} className="p-2 text-gray-600 hover:text-green-600 transition-colors" title="Share via WhatsApp" aria-label="Share via WhatsApp">
              <FaWhatsapp className="h-6 w-6" />
            </button>
            <button onClick={() => navigate('/cart')} className="relative p-2 rounded-full hover:bg-gray-100 transition-colors" aria-label={`Cart (${itemCount} items)`}>
              <FiShoppingCart className="h-6 w-6 text-gray-600" />
              {itemCount > 0 && (<span className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">{itemCount}</span>)}
            </button>
          </div>
        </div>
      </header>

      {/* MAIN CONTENT */}
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8 bg-white p-6 rounded-lg shadow-sm">

          {/* LEFT: IMAGES */}
          <div className="lg:w-1/2">
            <div className="sticky top-24">
              <div
                className="mb-4 aspect-[3/4] bg-gray-100 rounded-lg overflow-hidden relative cursor-zoom-in group"
                onMouseMove={handleMouseMove}
                onMouseEnter={() => setIsZoomed(true)}
                onMouseLeave={() => setIsZoomed(false)}
                onClick={() => openLightbox(mainImage)}
                ref={imageRef}
              >
                <img
                  src={mainImage || 'https://via.placeholder.com/600x800?text=Product+Image'}
                  alt={product.name}
                  className="w-full h-full object-contain"
                  onError={(e) => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/600x800?text=Product+Image'; }}
                />
                {isZoomed && <div className="absolute inset-0 overflow-hidden" ref={zoomRef}>
                  <div className="absolute w-[200%] h-[200%] transform origin-top-left" style={{ backgroundImage: `url(${mainImage})`, backgroundSize: '200%', backgroundPosition: `${zoomPosition.x}% ${zoomPosition.y}%`, left: `-${zoomPosition.x}%`, top: `-${zoomPosition.y}%` }} />
                </div>}
                <div className="absolute bottom-3 right-3 flex gap-2">
                  <button onClick={e => { e.stopPropagation(); toggleZoom(); }} className="bg-white/90 hover:bg-white p-2 rounded-full shadow-md transition-all opacity-0 group-hover:opacity-100" aria-label={isZoomed ? "Disable zoom" : "Enable zoom"}><FiZoomIn className="h-5 w-5 text-gray-700" /></button>
                  <button onClick={e => { e.stopPropagation(); shareOnWhatsApp(); }} className="bg-white/90 hover:bg-white p-2 rounded-full shadow-md transition-all opacity-0 group-hover:opacity-100" aria-label="Share product"><FiShare2 className="h-5 w-5 text-gray-700" /></button>
                </div>
                {isZoomed && <div className="absolute top-3 left-3 bg-black/70 text-white text-xs px-2 py-1 rounded">Scroll to zoom • Click for fullscreen</div>}
              </div>

              {/* THUMBNAILS */}
              <div className="grid grid-cols-4 gap-3 mt-3">
                {product.images.map((img, index) => (
                  <button key={index} onClick={() => { setMainImage(img); setLightboxImage(img); }} className={`aspect-[3/4] bg-gray-100 rounded overflow-hidden border-2 transition-all ${mainImage === img ? 'border-blue-500 shadow-md' : 'border-transparent hover:border-gray-300'}`} aria-label={`View image ${index + 1}`}>
                    <img src={img} alt={`Thumbnail ${index + 1}`} className="w-full h-full object-cover" onError={e => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/150x200?text=Thumb'; }} />
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT: DETAILS */}
          <div className="lg:w-1/2">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">{product.name}</h1>

            {/* SKU & STOCK - FIXED: Always show main product SKU */}
            <div className="flex items-center text-sm text-gray-500 mb-4">
              <span className="mr-4">SKU: {displaySKU}</span>
              <span className={`font-medium ${inStock ? 'text-green-600' : 'text-red-600'}`}>
                {inStock ? 'In Stock' : 'Out of Stock'}
                {selectedVariation?.stock > 0 && ` (${selectedVariation.stock} available)`}
              </span>
            </div>

            {/* PRICE */}
            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
              <span className="text-2xl font-bold text-blue-600">QAR {(selectedVariation?.price || product.base_price || 0).toFixed(2)}</span>
              {selectedVariation?.price !== product.base_price && (<span className="ml-2 text-lg text-gray-500 line-through">QAR {product.base_price.toFixed(2)}</span>)}
            </div>

            {/* COLOR SELECTION */}
            {product.availableColors.length > 0 && (
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-3">Color</h2>
                <div className="flex flex-wrap gap-2">
                  {product.availableColors.map(color => (
                    <button
                      key={color.color_id}
                      onClick={() => handleColorSelect(color)}
                      className={`w-10 h-10 rounded-full border-2 transition-all flex items-center justify-center ${selectedColor?.color_id === color.color_id ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200 hover:border-gray-400'}`}
                      title={color.name}
                      aria-label={`Select color ${color.name}`}
                    >
                      <ColorSwatch codes={Array.isArray(color.codes) ? color.codes : []} size={36} />
                    </button>
                  ))}
                </div>
                {selectedColor && <p className="mt-2 text-sm text-gray-600">Selected: {selectedColor.name}</p>}
              </div>
            )}

            {/* SIZE SELECTION */}
            {product.availableSizes.length > 0 && (
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-3">Size</h2>
                <div className="flex flex-wrap gap-2">
                  {product.availableSizes.map(size => {
                    const isAvailable = product.variations.some(v => v.size?.size_id === size.size_id && (!selectedColor || v.color?.color_id === selectedColor.color_id));
                    const displayName = formatSizeDisplay(size.name);
                    
                    return (
                      <button
                        key={size.size_id}
                        onClick={() => isAvailable && handleSizeSelect(size)}
                        className={`px-4 py-2 border rounded-md transition-all ${selectedSize?.size_id === size.size_id ? 'bg-blue-500 text-white' : 'bg-white text-gray-700'} ${isAvailable ? 'hover:border-gray-400 hover:bg-gray-50' : 'opacity-40 cursor-not-allowed'}`}
                        disabled={!isAvailable}
                        aria-label={`Select size ${displayName}`}
                        title={isAvailable ? `Select size ${displayName}` : 'Size not available'}
                      >
                        {displayName}
                      </button>
                    );
                  })}
                </div>
                {selectedSize && <p className="mt-2 text-sm text-gray-600">Selected: {formatSizeDisplay(selectedSize.name)}</p>}
                
                {/* Size guide reference */}
                
              </div>
            )}

            {/* QUANTITY */}
            <div className="mb-6 flex items-center gap-3">
              <span className="text-gray-900 font-medium">Quantity:</span>
              <div className="flex items-center border rounded-md overflow-hidden">
                <button 
                  onClick={() => handleQuantityChange(quantity - 1)} 
                  className="px-3 py-1 text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={quantity <= 1}
                >
                  -
                </button>
                <input 
                  type="number" 
                  value={quantity} 
                  min={1} 
                  max={maxQuantity} 
                  onChange={e => handleQuantityChange(Number(e.target.value))} 
                  className="w-12 text-center border-l border-r" 
                />
                <button 
                  onClick={() => handleQuantityChange(quantity + 1)} 
                  className="px-3 py-1 text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={quantity >= maxQuantity}
                >
                  +
                </button>
              </div>
              <span className="text-sm text-gray-500">Max: {maxQuantity}</span>
            </div>

            {/* ADD TO CART & BUY NOW */}
            <div className="flex flex-wrap gap-4 mb-6">
              <button 
                onClick={handleAddToCart} 
                disabled={!inStock} 
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {!inStock ? 'Out of Stock' : 'Add to Cart'}
              </button>
              <button 
                onClick={handleBuyNow} 
                disabled={!inStock} 
                className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {!inStock ? 'Out of Stock' : 'Buy Now'}
              </button>
            </div>

            {/* PRODUCT SPECIFICATIONS */}
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Product Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div className="flex">
                  <span className="font-medium text-gray-600 w-32">Product Code:</span>
                  <span className="text-gray-900">{displaySKU}</span>
                </div>
                <div className="flex">
                  <span className="font-medium text-gray-600 w-32">Brand:</span>
                  <span className="text-gray-900">{product.brand || 'Fight Gear'}</span>
                </div>
                {selectedColor && (
                  <div className="flex">
                    <span className="font-medium text-gray-600 w-32">Selected Color:</span>
                    <span className="text-gray-900">{selectedColor.name}</span>
                  </div>
                )}
                {selectedSize && (
                  <div className="flex">
                    <span className="font-medium text-gray-600 w-32">Selected Size:</span>
                    <span className="text-gray-900">{formatSizeDisplay(selectedSize.name)}</span>
                  </div>
                )}
                <div className="flex">
                  <span className="font-medium text-gray-600 w-32">Category:</span>
                  <span className="text-gray-900">{product.category_name || 'Sports Gear'}</span>
                </div>
                <div className="flex">
                  <span className="font-medium text-gray-600 w-32">Availability:</span>
                  <span className={`font-medium ${inStock ? 'text-green-600' : 'text-red-600'}`}>
                    {inStock ? 'In Stock' : 'Out of Stock'}
                  </span>
                </div>
              </div>
            </div>

            {/* DESCRIPTION */}
            {product.description && (
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900 mb-2">Description</h2>
                <p className="text-gray-700 whitespace-pre-line">{product.description}</p>
              </div>
            )}

          </div>
        </div>
      </div>

      {/* LIGHTBOX */}
      {showLightbox && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={closeLightbox}>
          <button onClick={closeLightbox} className="absolute top-4 right-4 text-white text-2xl"><FiX /></button>
          <img src={lightboxImage} alt={product.name} className="max-h-full max-w-full object-contain" />
        </div>
      )}
    </div>
  );
};

export default ProductDetailPage;
